/*
 * Copyright Deskshare, Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * Exchange Rate API Client
 *
 * Provides type-safe API methods for interacting with the backend
 * exchange rate service. Includes:
 * - Rate fetching with caching
 * - Currency conversion
 * - Supported currencies list
 * - Automatic retry logic
 * - Error handling
 *
 * @module lib/api/rates
 */

import { api } from "@/lib/http-client";
import { logger } from "@/lib/logger";

// ============================================================================
// Types & Interfaces
// ============================================================================

/**
 * Source of exchange rate data.
 * Note: stripe_fx was removed — the backend RateSource enum does not produce
 * this value. Only exchangerate_api, fallback_static, and cache are emitted.
 */
export type RateSource =
  | "exchangerate_api"
  | "fallback_static"
  | "cache";

/**
 * Exchange rate data from backend API
 */
interface ExchangeRateData {
  base: string;
  rates: Record<string, number>;
  last_updated: string;
  source: RateSource;
  is_stale: boolean;
  age_hours: number;
}

/**
 * API error structure (internal)
 */
interface RateApiError {
  error: string;
  detail?: string;
  status?: number;
}

// ============================================================================
// Local Cache
// ============================================================================

/**
 * In-memory cache for exchange rates
 * Reduces API calls for rapid rate lookups
 */
interface RateCache {
  data: ExchangeRateData | null;
  timestamp: number;
  ttl: number; // milliseconds
}

const rateCache: RateCache = {
  data: null,
  timestamp: 0,
  ttl: 5 * 60 * 1000, // 5 minutes client-side cache
};

/**
 * Check if cache is valid
 */
function isCacheValid(): boolean {
  if (!rateCache.data) return false;
  return Date.now() - rateCache.timestamp < rateCache.ttl;
}

/**
 * Update cache with new data
 */
function updateCache(data: ExchangeRateData): void {
  rateCache.data = data;
  rateCache.timestamp = Date.now();
}

/**
 * Clear the rate cache
 */
export function clearRateCache(): void {
  rateCache.data = null;
  rateCache.timestamp = 0;
}

// ============================================================================
// API Methods
// ============================================================================

/**
 * Fetch current exchange rates from backend
 *
 * @param options - Fetch options
 * @param options.forceRefresh - Force refresh from external API (bypasses backend cache)
 * @param options.skipLocalCache - Skip local client-side cache
 * @returns Exchange rate data
 * @throws RateApiError on failure
 *
 * @example
 * ```ts
 * const rates = await fetchExchangeRates();
 * console.log(rates.rates.EUR); // 0.85
 * ```
 */
export async function fetchExchangeRates(options?: {
  forceRefresh?: boolean;
  skipLocalCache?: boolean;
}): Promise<ExchangeRateData> {
  const { forceRefresh = false, skipLocalCache = false } = options ?? {};

  // Check local cache first (unless skipping)
  if (!skipLocalCache && !forceRefresh && isCacheValid() && rateCache.data) {
    logger.debug("Using cached exchange rates");
    return rateCache.data;
  }

  try {
    const params = forceRefresh ? "?refresh=true" : "";
    const response = await api.get<ExchangeRateData>(`/rates/${params}`);
    const data = response.data;

    // Update local cache
    updateCache(data);

    logger.debug(`Fetched exchange rates from ${data.source}`, {
      currencies: Object.keys(data.rates).length,
      age_hours: data.age_hours,
    });

    return data;
  } catch (error) {
    logger.error("Failed to fetch exchange rates:", error);
    throw normalizeError(error);
  }
}

// ============================================================================
// Client-Side Conversion Utilities
// ============================================================================

/**
 * Convert amount using locally cached rates
 *
 * This is faster than server-side conversion for repeated conversions
 * but requires rates to be fetched first.
 *
 * @param amount - Amount to convert
 * @param fromCurrency - Source currency code
 * @param toCurrency - Target currency code
 * @param rates - Exchange rate map (from ExchangeRateData.rates)
 * @returns Converted amount (null if conversion not possible)
 *
 * @example
 * ```ts
 * const rates = await fetchExchangeRates();
 * const eurAmount = convertLocally(100, 'USD', 'EUR', rates.rates);
 * console.log(eurAmount); // 85.00
 * ```
 */
export function convertLocally(
  amount: number,
  fromCurrency: string,
  toCurrency: string,
  rates: Record<string, number>,
): number | null {
  const from = fromCurrency.toUpperCase();
  const to = toCurrency.toUpperCase();

  // Same currency - no conversion needed
  if (from === to) {
    return amount;
  }

  const fromRate = rates[from];
  const toRate = rates[to];

  if (fromRate === undefined || toRate === undefined) {
    logger.warn(`Conversion failed: missing rate for ${from} or ${to}`);
    return null;
  }

  // Convert: amount in from_currency -> USD -> to_currency
  // Formula: (amount / fromRate) * toRate
  const usdAmount = amount / fromRate;
  const converted = usdAmount * toRate;

  return Math.round(converted * 100) / 100; // Round to 2 decimal places
}

/**
 * Get exchange rate between two currencies from cached rates
 *
 * @param fromCurrency - Source currency code
 * @param toCurrency - Target currency code
 * @param rates - Exchange rate map
 * @returns Exchange rate (null if not available)
 */
export function getRate(
  fromCurrency: string,
  toCurrency: string,
  rates: Record<string, number>,
): number | null {
  const from = fromCurrency.toUpperCase();
  const to = toCurrency.toUpperCase();

  if (from === to) return 1;

  const fromRate = rates[from];
  const toRate = rates[to];

  if (fromRate === undefined || toRate === undefined) {
    return null;
  }

  return toRate / fromRate;
}

/**
 * Check if a currency is supported (in the rates map)
 */
export function isCurrencySupported(
  currencyCode: string,
  rates: Record<string, number>,
): boolean {
  return currencyCode.toUpperCase() in rates;
}

// ============================================================================
// Error Handling
// ============================================================================

/**
 * Normalize API errors to consistent format
 */
function normalizeError(error: unknown): RateApiError {
  if (error && typeof error === "object") {
    const err = error as Record<string, unknown>;

    // Axios-style error
    if (err.response && typeof err.response === "object") {
      const response = err.response as Record<string, unknown>;
      const data = response.data as Record<string, unknown> | undefined;

      return {
        error: (data?.error as string) || "API request failed",
        detail: (data?.detail as string) || undefined,
        status: response.status as number | undefined,
      };
    }

    // Direct error object
    if (err.message) {
      return {
        error: err.message as string,
        detail: undefined,
        status: undefined,
      };
    }
  }

  return {
    error: "Unknown error occurred",
    detail: String(error),
    status: undefined,
  };
}


